'use client'

import type { BurndownPoint, VelocityPoint } from '@/lib/dev-sprint'

export interface Metrics {
  totalPts: number
  donePts: number
  progressPct: number
  teamOnTimePct: number | null
  burndownSeries: BurndownPoint[]
  velocityData: VelocityPoint[]
}

export function KpiRow({ metrics, capacity, selectedSprintId }: { metrics: Metrics; capacity: number; selectedSprintId: string | null }) {
  const { totalPts, donePts, progressPct, teamOnTimePct, burndownSeries, velocityData } = metrics
  const current = velocityData.find((v) => v.sprintId === selectedSprintId) ?? velocityData.find((v) => v.current)
  const recent = velocityData.slice(-5)

  return (
    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Progress */}
      <Card title="Progress Sprint">
        <div className="flex items-end gap-2">
          <span className="text-[28px] font-extrabold text-[#1A1916] leading-none tabular-nums">{progressPct}%</span>
          <span className="text-[12px] text-[#8B8A85] mb-0.5 tabular-nums">{donePts}/{totalPts} pts</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-[#F0EEE7] overflow-hidden">
          <div className="h-full rounded-full bg-[#34C77B]" style={{ width: `${progressPct}%` }} />
        </div>
      </Card>

      {/* Burndown */}
      <Card title="Burndown">
        <MiniBurndown series={burndownSeries} />
      </Card>

      {/* Velocity */}
      <Card title="Velocity">
        <div className="flex items-end gap-2">
          <span className="text-[28px] font-extrabold text-[#1A1916] leading-none tabular-nums">{current?.points ?? 0}</span>
          <span className="text-[12px] text-[#8B8A85] mb-0.5">pts sprint terpilih</span>
        </div>
        <MiniBars data={recent} />
      </Card>

      {/* On-time */}
      <Card title="On-time Tim">
        <div className="flex items-end gap-2">
          <span className="text-[28px] font-extrabold leading-none tabular-nums" style={{ color: onTimeColor(teamOnTimePct) }}>
            {teamOnTimePct == null ? '—' : `${teamOnTimePct}%`}
          </span>
        </div>
        <p className="text-[11px] text-[#AEADA8] mt-2">Kapasitas {capacity} pts/dev/minggu</p>
      </Card>
    </div>
  )
}

function onTimeColor(pct: number | null): string {
  if (pct == null) return '#AEADA8'
  if (pct >= 80) return '#1E7A46'
  if (pct >= 50) return '#9A6B00'
  return '#C0362C'
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#AEADA8] mb-2">{title}</p>
      {children}
    </div>
  )
}

function MiniBurndown({ series }: { series: BurndownPoint[] }) {
  const W = 160, H = 44
  if (series.length < 2) return <div className="h-[44px] flex items-center text-[12px] text-[#AEADA8]">Belum ada data</div>
  const max = Math.max(1, ...series.map((p) => p.ideal))
  const x = (i: number) => (i / (series.length - 1)) * W
  const y = (v: number) => H - (v / max) * H
  const idealPath = series.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.ideal).toFixed(1)}`).join(' ')
  const actualPts = series.filter((p) => p.actual != null)
  const actualPath = actualPts.map((p, i) => `${i ? 'L' : 'M'}${x(series.indexOf(p)).toFixed(1)},${y(p.actual as number).toFixed(1)}`).join(' ')
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[44px]" preserveAspectRatio="none">
      <path d={idealPath} fill="none" stroke="#D8D6CF" strokeWidth={1.5} strokeDasharray="3 3" />
      {actualPts.length > 1 && <path d={actualPath} fill="none" stroke="#1A1916" strokeWidth={2} />}
    </svg>
  )
}

function MiniBars({ data }: { data: VelocityPoint[] }) {
  if (data.length === 0) return <div className="h-6" />
  const max = Math.max(1, ...data.map((d) => d.points))
  return (
    <div className="mt-2 flex items-end gap-1 h-6">
      {data.map((d) => (
        <div
          key={d.sprintId}
          title={`${d.name}: ${d.points} pts`}
          className="flex-1 rounded-sm"
          style={{ height: `${Math.max(8, (d.points / max) * 100)}%`, backgroundColor: d.current ? '#D4A843' : '#E0DED7' }}
        />
      ))}
    </div>
  )
}
